import React, { useMemo } from 'react';

import pdfSvg from '@assets/img/icons/pdf-padding.svg';
import ClearIcon from '@material-ui/icons/Clear';
import type { LocalFile } from '@models/next/FileType';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    width: 156,
    height: 171,
    background: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[300]}`,
    boxSizing: 'border-box',
    borderRadius: 4,
    padding: '4px 4px',
  },
  header: {
    ...common.flexInline,
    ...common.justifyBetween,
  },
  title: {
    ...common.exSmallerText,
    color: theme.palette.grey[500],
  },
  photo: {
    ...common.flexColumn,
    ...common.justifyEnd,
    ...common.itemsCenter,
    width: '100%',
    height: '144px',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    cursor: 'pointer',
    paddingBottom: '1rem',
    paddingLeft: '1rem',
    paddingRight: '0.5rem',
  },
  backdrop: {
    position: 'relative',
    zIndex: 999999,
    width: '100%',
    height: '100%',
  },
  removeBtn: {
    width: 16,
    height: 16,
    cursor: 'pointer',
    color: theme.palette.grey[500],
  },
  name: {
    ...common.middleText,
    ...common.whitespacePre,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

export interface UploadImagePreview {
  title: string;
  image: LocalFile;
  onRemove?: () => void;
}

export const UploadImagePreview: React.FC<UploadImagePreview> = (props) => {
  const { title, image, onRemove } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const imageUrl = useMemo(() => {
    if (image.remote?.endsWith('.pdf') || image.file!.type === 'application/pdf') {
      return pdfSvg;
    }

    if (image.remote) {
      if (image.remote.endsWith('.pdf')) {
        return pdfSvg;
      }
      return image.remote;
    }

    return URL.createObjectURL(image.file!);
  }, [image]);

  const fileUrl = useMemo(() => {
    if (image.remote) {
      return image.remote;
    }

    return URL.createObjectURL(image.file!);
  }, [image]);

  const isPdf = imageUrl === pdfSvg;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.title}>{title}</div>
        <div title={formStrings.delete}>
          <ClearIcon fontSize="small" className={classes.removeBtn} onClick={onRemove} />
        </div>
      </div>
      <div
        className={classes.photo}
        id={imageUrl}
        style={{
          backgroundImage: `url('${imageUrl}')`,
        }}
        onClick={() => window.open(fileUrl, '_blank')}
      >
        {isPdf && (
          <div className={classes.name}>
            {image.file?.name ?? image.remote?.split('/')[image.remote?.split('/').length - 1]}
          </div>
        )}
      </div>
    </div>
  );
};
